import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { get } from "../../redux/ApiHelper";
import { startLoading, stopLoading } from "../../redux/slices/WindowSlice";
import { errorToast } from "../../utils/toast.util";
import {
  TuitionPeriodDetailsResponse,
  TuitionPeriodInterface,
  TuitionPeriodPerson,
} from "../../interfaces/tuitionPeriod.interface";
import { TeachingSetInterface } from "../../interfaces/teaching-set.interface";
import TeachingSetsList from "../teacher/components/TeachingSetsList";
import "./TuitionPeriodDetails.scss";

const formatDate = (value?: string | Date) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", {
        dateStyle: "medium",
      })
    : "N/A";

const formatDateTime = (value?: string | Date) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "N/A";

const asPerson = (value: unknown): TuitionPeriodPerson =>
  value && typeof value === "object" ? (value as TuitionPeriodPerson) : {};

const resolveGcCharge = (value: TuitionPeriodInterface["gcChargePerClass"]) => {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && typeof value.coin === "number") {
    return value.coin;
  }
  return null;
};

const resolveLinkedTeachingSet = (
  teachingSetId: unknown
): TeachingSetInterface | null => {
  if (!teachingSetId) return null;
  if (typeof teachingSetId === "object" && teachingSetId !== null) {
    return teachingSetId as TeachingSetInterface;
  }
  return null;
};

const StatusPill = ({
  value,
  tone = "neutral",
}: {
  value?: string | null;
  tone?: "success" | "warning" | "danger" | "neutral";
}) => {
  const toneClass =
    tone === "success"
      ? "bg-green-900/60 text-green-300"
      : tone === "warning"
        ? "bg-amber-900/60 text-amber-300"
        : tone === "danger"
          ? "bg-red-900/60 text-red-300"
          : "bg-gray-700 text-gray-300";

  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${toneClass}`}
    >
      {value || "N/A"}
    </span>
  );
};

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-gray-700 last:border-0">
    <span className="text-xs text-gray-500 shrink-0">{label}</span>
    <span className="text-sm text-gray-200 text-right break-words">
      {value ?? "N/A"}
    </span>
  </div>
);

const PersonCard = ({
  title,
  person,
}: {
  title: string;
  person: TuitionPeriodPerson;
}) => (
  <div className="tp-card">
    <h3 className="tp-card__title">{title}</h3>
    <InfoRow label="Name" value={person.name || "N/A"} />
    <InfoRow label="Email" value={person.email || "N/A"} />
    <InfoRow
      label="Phone"
      value={
        person.phoneNumber != null
          ? `+${person.phoneCode ?? "91"} ${person.phoneNumber}`
          : "N/A"
      }
    />
    {person._id && (
      <InfoRow
        label="ID"
        value={<span className="text-xs font-mono text-gray-400">{person._id}</span>}
      />
    )}
  </div>
);

const TuitionPeriodDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState<TuitionPeriodDetailsResponse | null>(null);

  useEffect(() => {
    if (!id) return;
    dispatch(startLoading());
    get(`/admin/tuition/tuition-periods/${id}`)
      .then((res: TuitionPeriodDetailsResponse) => {
        setData(res);
      })
      .catch(() => {
        errorToast("Failed to load tuition period details");
        navigate("/tuition-periods");
      })
      .finally(() => {
        dispatch(stopLoading());
      });
  }, [id, dispatch, navigate]);

  if (!data?.period) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-lg font-semibold text-gray-300">Loading...</div>
      </div>
    );
  }

  const {
    period,
    classes,
    tuitionRequest,
    parentShadowWallet,
    teacherShadowWallet,
    teachingSets = [],
  } = data;
  const teacher = asPerson(period.teacher);
  const student = asPerson(period.student);
  const parent = asPerson(period.parent);
  const gcCharge = resolveGcCharge(period.gcChargePerClass);
  const canceled =
    period.cancel?.isCanceledByParent ||
    period.cancel?.isCanceledByTeacher ||
    period.cancel?.isCanceledByAdmin;
  const linkedTeachingSet = resolveLinkedTeachingSet(
    tuitionRequest?.teachingSetId
  );
  const linkedTeachingSetId =
    linkedTeachingSet?._id ||
    (typeof tuitionRequest?.teachingSetId === "string"
      ? tuitionRequest.teachingSetId
      : null);

  const teacherStatusTone =
    period.teacherStatus === "approved"
      ? "success"
      : period.teacherStatus === "rejected"
        ? "danger"
        : "warning";

  return (
    <div className="tuition-period-details p-4 md:p-6 space-y-5 text-gray-200">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 text-left">
          <button
            type="button"
            onClick={() => navigate("/tuition-periods")}
            className="tp-back-btn"
          >
            ← Back to Tuition Periods
          </button>
          <h1 className="text-2xl font-bold text-gray-100">
            {period.subjects?.length
              ? period.subjects.join(", ")
              : "Tuition Period"}
          </h1>
          <p className="text-xs font-mono text-gray-500 mt-1 break-all">
            {period._id}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <StatusPill value={period.teacherStatus} tone={teacherStatusTone} />
          {period.preferredClassMode && (
            <StatusPill value={period.preferredClassMode} />
          )}
          {canceled && <StatusPill value="Canceled" tone="danger" />}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PersonCard title="Teacher" person={teacher} />
        <PersonCard title="Student" person={student} />
        <PersonCard title="Parent" person={parent} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="tp-card">
          <h3 className="tp-card__title">Period metrics</h3>
          <InfoRow
            label="Classes available / total"
            value={`${period.availableNumberOfClasses ?? 0} / ${period.totalNumberOfClasses ?? 0}`}
          />
          <InfoRow
            label="GC charge / class"
            value={gcCharge != null ? gcCharge : "N/A"}
          />
          <InfoRow label="Mode" value={period.preferredClassMode || "N/A"} />
          <InfoRow label="Created" value={formatDate(period.createdAt)} />
          <InfoRow label="Updated" value={formatDate(period.updatedAt)} />
          <InfoRow
            label="TR withdrawn by parent"
            value={period.isTRWithdrawnByParent ? "Yes" : "No"}
          />
          {period.cancel?.reason && (
            <InfoRow label="Cancel reason" value={period.cancel.reason} />
          )}
        </div>

        <div className="tp-card">
          <h3 className="tp-card__title">Preferred schedule</h3>
          {period.preferredSchedule?.length ? (
            <div className="space-y-2">
              {period.preferredSchedule.map((slot, index) => {
                const schedule =
                  slot && typeof slot === "object" && "day" in slot
                    ? slot
                    : null;
                if (!schedule) return null;
                return (
                  <div
                    key={index}
                    className="flex justify-between text-sm bg-gray-800/60 rounded-lg px-3 py-2"
                  >
                    <span className="font-medium text-gray-200">
                      {schedule.day}
                    </span>
                    <span className="text-gray-400">
                      {schedule.from || "--"} – {schedule.to || "--"}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No schedule provided</p>
          )}
        </div>
      </div>

      <div className="tp-card">
        <h3 className="tp-card__title">Tuition request</h3>
        {tuitionRequest ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <InfoRow
              label="Request ID"
              value={
                <span className="text-xs font-mono text-gray-400">
                  {tuitionRequest._id || "N/A"}
                </span>
              }
            />
            <InfoRow
              label="Required classes"
              value={tuitionRequest.requiredNumberOfClasses}
            />
            <InfoRow
              label="Admin status"
              value={
                <StatusPill
                  value={tuitionRequest.adminStatus}
                  tone={
                    tuitionRequest.adminStatus === "approved"
                      ? "success"
                      : tuitionRequest.adminStatus === "rejected"
                        ? "danger"
                        : "warning"
                  }
                />
              }
            />
            <InfoRow
              label="Teacher status"
              value={
                <StatusPill
                  value={tuitionRequest.teacherStatus}
                  tone={
                    tuitionRequest.teacherStatus === "approved"
                      ? "success"
                      : tuitionRequest.teacherStatus === "rejected"
                        ? "danger"
                        : "warning"
                  }
                />
              }
            />
            <InfoRow
              label="Subjects"
              value={
                tuitionRequest.preferredSubjects?.length
                  ? tuitionRequest.preferredSubjects.join(", ")
                  : "N/A"
              }
            />
            <InfoRow
              label="Class mode"
              value={
                (tuitionRequest as { preferredClassMode?: string })
                  .preferredClassMode || "N/A"
              }
            />
            <InfoRow
              label="Travel allowance (GC)"
              value={
                (tuitionRequest as { travelAllowanceInGCPerClass?: number })
                  .travelAllowanceInGCPerClass ?? "N/A"
              }
            />
            <InfoRow
              label="Created"
              value={formatDate(tuitionRequest.createdAt)}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No tuition request linked to this period.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="tp-card">
          <h3 className="tp-card__title">Parent shadow wallet</h3>
          {parentShadowWallet ? (
            <>
              <InfoRow
                label="Current coins"
                value={parentShadowWallet.currentCoins ?? 0}
              />
              <InfoRow
                label="Total allocated"
                value={parentShadowWallet.totalCoinsAllocated ?? 0}
              />
              <InfoRow
                label="GC / class"
                value={parentShadowWallet.gcChargePerClass ?? "N/A"}
              />
              <InfoRow
                label="Free sessions"
                value={parentShadowWallet.noOfSessionFree ?? 0}
              />
              <InfoRow
                label="Active"
                value={parentShadowWallet.isActive ? "Yes" : "No"}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">No parent wallet found.</p>
          )}
        </div>

        <div className="tp-card">
          <h3 className="tp-card__title">Teacher shadow wallet</h3>
          {teacherShadowWallet ? (
            <>
              <InfoRow label="Coins" value={teacherShadowWallet.coins ?? 0} />
              <InfoRow
                label="Security coins"
                value={teacherShadowWallet.availableSecurityCoins ?? 0}
              />
              <InfoRow
                label="Bonus"
                value={teacherShadowWallet.availableBonus ?? 0}
              />
              <InfoRow
                label="Teached hours"
                value={teacherShadowWallet.teachedHours ?? 0}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">No teacher wallet found.</p>
          )}
        </div>
      </div>

      <div className="tp-card">
        <h3 className="tp-card__title">
          Teaching sets ({teachingSets.length})
        </h3>
        {linkedTeachingSetId && (
          <p className="text-sm text-gray-400 mb-1">
            Linked to this request:{" "}
            <span className="text-yellow-400">
              {linkedTeachingSet
                ? `${linkedTeachingSet.board || "N/A"} / ${
                    linkedTeachingSet.level?.replace("_", " ") || "N/A"
                  }`
                : "N/A"}
            </span>{" "}
            <span className="font-mono text-xs text-gray-500">
              ({linkedTeachingSetId})
            </span>
          </p>
        )}
        <TeachingSetsList sets={teachingSets} />
      </div>

      <div className="tp-card overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <h3 className="tp-card__title mb-0">Classes ({classes.length})</h3>
        </div>
        {classes.length === 0 ? (
          <p className="text-sm text-gray-500">No classes for this period.</p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="min-w-full text-sm text-gray-300">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-700">
                  <th className="py-2 px-2">#</th>
                  <th className="py-2 px-2">Day</th>
                  <th className="py-2 px-2">Date</th>
                  <th className="py-2 px-2">Time</th>
                  <th className="py-2 px-2">Mode</th>
                  <th className="py-2 px-2">Teacher</th>
                  <th className="py-2 px-2">Student</th>
                  <th className="py-2 px-2">GC</th>
                  <th className="py-2 px-2">Pin</th>
                  <th className="py-2 px-2">Actual</th>
                </tr>
              </thead>
              <tbody>
                {classes.map((tuitionClass) => {
                  const schedule = tuitionClass.tuitionClassSchedule?.schedule;
                  return (
                    <tr
                      key={tuitionClass._id}
                      className="border-b border-gray-700/80 hover:bg-gray-800/40"
                    >
                      <td className="py-2 px-2 font-medium text-gray-100">
                        {tuitionClass.currentTuitionClassNo}
                      </td>
                      <td className="py-2 px-2">{schedule?.day || "N/A"}</td>
                      <td className="py-2 px-2">
                        {formatDate(schedule?.date)}
                      </td>
                      <td className="py-2 px-2 whitespace-nowrap">
                        {schedule?.from || "--"} – {schedule?.to || "--"}
                      </td>
                      <td className="py-2 px-2 capitalize">
                        {tuitionClass.classMode || "N/A"}
                      </td>
                      <td className="py-2 px-2">
                        {tuitionClass.isTeacherPresent ? "Present" : "Absent"}
                      </td>
                      <td className="py-2 px-2">
                        {tuitionClass.isStudentPresent ? "Present" : "Absent"}
                      </td>
                      <td className="py-2 px-2">
                        {tuitionClass.gcChargePerClass ?? "N/A"}
                        {tuitionClass.isGuruCoinDeducted ? " ✓" : ""}
                      </td>
                      <td className="py-2 px-2 font-mono text-xs text-gray-400">
                        {tuitionClass.pinToken || "—"}
                      </td>
                      <td className="py-2 px-2 text-xs text-gray-400">
                        {tuitionClass.tuitionClassSchedule?.actualStartTime
                          ? formatDateTime(
                              tuitionClass.tuitionClassSchedule.actualStartTime
                            )
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-500">
        Need class schedules overview?{" "}
        <Link className="text-yellow-400 hover:underline" to="/tuition-classes">
          Open Tuition Classes
        </Link>
      </div>
    </div>
  );
};

export default TuitionPeriodDetails;
